import { Injectable } from "@angular/core";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { AdaptClientConfiguration, AdaptProject } from "@common/configuration/adapt-client-configuration";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";

@Injectable({
    providedIn: "root",
})
export class StrategyAuthService {
    public static readonly ReadStrategicDashboard = "readStrategicDashboard";
    public static readonly ReadBullseye = "readBullseye";
    public static readonly EditBullseye = "editBullseye";
    public static readonly ReadStrategyBoard = "readStrategyBoard";
    public static readonly EditStrategyBoard = "editStrategyBoard";
    public static readonly ReadStrategicInputs = "readStrategicInputs";
    public static readonly EditStrategicInputs = "editStrategicInputs";
    public static readonly ReadStrategicGoals = "readStrategicGoals";
    public static readonly EditStrategicGoals = "editStrategicGoals";
    public static readonly ReadStrategicAnchors = "readStrategicAnchors";
    public static readonly EditStrategicAnchors = "editStrategicAnchors";

    public static registerAccess(authorisationService: AuthorisationService) {
        this.registerBullseye(authorisationService);
        this.registerStrategyBoard(authorisationService);
        this.registerStrategicInputs(authorisationService);
        this.registerStrategicGoals(authorisationService);
        this.registerStrategicAnchors(authorisationService);
        this.registerStrategicDashboard(authorisationService);
    }

    private static registerBullseye(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            StrategyAuthService.ReadBullseye,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategyBullseyeRead,
                    FeaturePermissionName.StrategyBullseyeEdit,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            StrategyAuthService.EditBullseye,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategyBullseyeEdit,
                ],
            },
        );
    }

    private static registerStrategyBoard(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            StrategyAuthService.ReadStrategyBoard,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategyBoardRead,
                    FeaturePermissionName.StrategyBoardEdit,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            StrategyAuthService.EditStrategyBoard,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategyBoardEdit,
                ],
            },
        );
    }

    private static registerStrategicInputs(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            StrategyAuthService.ReadStrategicInputs,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategicInputsRead,
                    FeaturePermissionName.StrategicInputsEdit,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            StrategyAuthService.EditStrategicInputs,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategicInputsEdit,
                ],
            },
        );
    }

    private static registerStrategicGoals(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            StrategyAuthService.ReadStrategicGoals,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategicGoalsRead,
                    FeaturePermissionName.StrategicGoalsEdit,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            StrategyAuthService.EditStrategicGoals,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategicGoalsEdit,
                ],
            },
        );
    }

    private static registerStrategicAnchors(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            StrategyAuthService.ReadStrategicAnchors,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategicAnchorsRead,
                    FeaturePermissionName.StrategicAnchorsEdit,
                ],
            },
        );
        authorisationService.registerAccessVerifier(
            StrategyAuthService.EditStrategicAnchors,
            {
                requirePermissions: [
                    FeaturePermissionName.StrategicAnchorsEdit,
                ],
            },
        );
    }

    private static registerStrategicDashboard(authorisationService: AuthorisationService) {
        // until CM-6505 is implemented, the strategic dashboard will be shown if the organisation has at least one of these features active
        authorisationService.registerAccessVerifier(
            StrategyAuthService.ReadStrategicDashboard,
            {
                requirePermissions: [
                    // quick hack to have this enabled by default in HQ
                    // since none of these features are enabled in a new hq org
                    // the dashboard points you to pathways to complete so an empty dashboard is more useful in HQ
                    ...(AdaptClientConfiguration.AdaptProjectName === AdaptProject.Alto
                        ? [FeaturePermissionName.PlatformStandardRead]
                        : []),
                    FeaturePermissionName.CompassGuidingPhilosophyRead,
                    FeaturePermissionName.CompassGuidingPhilosophyEdit,
                    FeaturePermissionName.CompassValuesConstitutionRead,
                    FeaturePermissionName.CompassValuesConstitutionEdit,
                    FeaturePermissionName.StrategyBullseyeRead,
                    FeaturePermissionName.StrategyBullseyeEdit,
                    FeaturePermissionName.StrategicGoalsRead,
                    FeaturePermissionName.StrategicGoalsEdit,
                    FeaturePermissionName.StrategicAnchorsRead,
                    FeaturePermissionName.StrategicAnchorsEdit,
                ],
            },
        );
    }
}
